import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import { BuildingThreeIcon, StarIcon, TrainIcon, UserTwoIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { toast } from 'sonner'

import { API } from '../../../api/apis.config'
import { diffuseEnrollments } from '../../../api/centers/diffuse-enrollments'
import { diffuseTripBookings } from '../../../api/centers/diffuse-trip-booking'
import { getEnrollmentCenters } from '../../../api/centers/get-enrollment-centers'
import { diffuseCenterEndOfStayAppreciations } from '../../../api/end-of-journey-reviews/sharing/diffuse-center-end-of-stay-appreciations'
import FilterDrawer from '../../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../../components/elements/dynamic-table'
import { countFilters } from '../../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../../components/elements/nav-bar'
import { CentersFilter } from '../../../components/pages/centers/center-filter'
import { NotDiffusedCentersModal } from '../../../components/pages/ols/centers/not-diffused-centers-modal'
import { Paths } from '../../../lib/constants/path'
import { useFilters } from '../../../lib/hooks/use-filters'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../lib/hooks/use-translate'
import { RoleEnum } from '../../../lib/types/enums/users/role.enum'
import { Center, DiffuseTripBookingsResponse } from '../../../lib/types/models/center/center'
import { CenterFilter } from '../../../lib/types/models/center/center-filter'
import {
  transformCentersFiltersToUrl,
  transformUrlToCenterFilters
} from '../../../lib/utils/filters-mapping/centers'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { ChangeFrenchLeaderPhoneNumberModel } from './change-french-leader-phone-number-model'
import { ChangeHostFamilyModel } from './change-host-family-model'
import {
  placedHostFamilyFilter,
  studentCampusResidenceCollegeFilter,
  studentHostFamilyFilter
} from './utils/constants'
import { constructOlCenterPath } from './utils/functions'
import { getOlCenterColumns } from './utils/ol-center-columns'

const OlCenterListPage = () => {
  const { t } = useTranslate()
  const { navigateWithEvent } = useNavigator()
  const tableRef = useRef<DynamicTableRef<Center>>(null)
  const [isTableLoading, setIsTableLoading] = useState(false)
  const { modalState, toggleModal, closeModel, selectedData } = useModalManager<
    'filters' | 'mobile-number-for-french-leaders' | 'host-family',
    Center
  >(['filters', 'mobile-number-for-french-leaders', 'host-family'])

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<CenterFilter>({
      initialFilters: { isOlPresent: true, year: new Date().getFullYear() },
      options: { allowSearchParam: true },
      transformFilterToUrl: transformCentersFiltersToUrl,
      transformUrlToFilter: transformUrlToCenterFilters
    })

  const [notDiffusedCenters, setNotDiffusedCenters] = useState<
    DiffuseTripBookingsResponse['notDiffusedCenters']
  >([])

  const diffuseEnrollmentsMutation = useMutation({
    mutationKey: ['diffuse-enrollments'],
    mutationFn: diffuseEnrollments,
    onSuccess: () => {
      tableRef.current?.clearSelection()
      toast.success('La diffusion des jeunes à bien été effectuée.')
    }
  })

  const diffuseTripBookingsMutation = useMutation({
    mutationKey: ['diffuse-trip-bookings'],
    mutationFn: diffuseTripBookings,
    onError: showAxiosError,
    onSuccess: ({ diffusedCount, notDiffusedCenters }) => {
      tableRef.current?.clearSelection()
      if (diffusedCount > 0)
        toast.success('Les infos voyage ont bien été envoyées au(x) prestataire(s).')
      setNotDiffusedCenters(notDiffusedCenters)
    }
  })

  // "Diffusion des appréciations de fin de séjour": the provider sees its TEFL and host family ratings
  const diffuseEndOfStayAppreciationsMutation = useMutation({
    mutationKey: ['diffuse-center-end-of-stay-appreciations'],
    mutationFn: diffuseCenterEndOfStayAppreciations,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.clearSelection()
      toast.success(t('ol.center.end.of.stay.appreciations.diffused.success'))
    }
  })

  const { data: countStudentCampusResidenceCollege } = useQuery({
    queryKey: ['enrollment-centers', studentCampusResidenceCollegeFilter],
    queryFn: () => getEnrollmentCenters(studentCampusResidenceCollegeFilter)
  })

  const { data: countHostFamily } = useQuery({
    queryKey: ['enrollment-centers', studentHostFamilyFilter],
    queryFn: () => getEnrollmentCenters(studentHostFamilyFilter)
  })

  const { data: placedHostFamily } = useQuery({
    queryKey: ['enrollment-centers', placedHostFamilyFilter],
    queryFn: () => getEnrollmentCenters(placedHostFamilyFilter)
  })

  const nonPlacedHostFamily = (countHostFamily?.total ?? 0) - (placedHostFamily?.total ?? 0)

  return (
    <div>
      <UserNavBar
        title="Monitoring des centres & prestataires étrangers"
        icon={BuildingThreeIcon}
      />
      <div className="h-4" />
      <Box>
        <DynamicDataTable<Center, CenterFilter>
          onLoadingChange={setIsTableLoading}
          title={'Tous les centres'}
          className="p-0"
          ref={tableRef}
          deleteMessage={{ name: 'centre', attributeName: 'name' }}
          sorting={{ id: 'startDate', desc: false }}
          columns={getOlCenterColumns(t, [RoleEnum.ADMIN], true, {
            onTelephoneClick: (center) => toggleModal('mobile-number-for-french-leaders', center),
            onTeachingHallClick: (center) => toggleModal('host-family', center)
          })}
          selectable
          label={() => (
            <div className="flex items-center gap-2">
              <Badge
                variant="warning"
                text={`Jeunes en collège / campus / résidence : ${countStudentCampusResidenceCollege?.total ?? '-'}`}
              />
              <Badge
                variant="warning"
                text={`Jeunes en famille : ${countHostFamily?.total ?? 0} (Placés : ${placedHostFamily?.total ?? 0}) - (Non placés : ${nonPlacedHostFamily}) `}
              />
            </div>
          )}
          allowSearchParam
          actionLabel={
            <Typography variant="tag" color={tailwindColors.grey.lighter}>
              Diffusion au presta
            </Typography>
          }
          onRowClick={(center, event) =>
            navigateWithEvent(constructOlCenterPath(Paths.OL_CENTER_PLACEMENTS, center.id), event)
          }
          filters={selectedFilter}
          filtersLength={countFilters(selectedFilter, ['isOlPresent'])}
          onFiltersClick={() => toggleModal('filters')}
          endpoint={API.centers.getAll.url}
          actions={[
            {
              label: 'Jeunes',
              icon: UserTwoIcon,
              variant: 'ghost',
              type: 'multiple',
              refetch: true,
              fn: (rows) => diffuseEnrollmentsMutation.mutateAsync({ ids: rows.map((c) => c.id) })
            },
            {
              label: 'Voyages',
              icon: TrainIcon,
              variant: 'ghost',
              type: 'multiple',
              refetch: true,
              fn: (centers) => diffuseTripBookingsMutation.mutateAsync(centers.map((c) => c.id))
            },
            {
              label: 'Notes/Satisfaction',
              icon: StarIcon,
              variant: 'ghost',
              type: 'multiple',
              refetch: true,
              fn: (centers) =>
                diffuseEndOfStayAppreciationsMutation.mutateAsync({
                  ids: centers.map((c) => c.id),
                  diffused: true
                })
            }
          ]}
        />
      </Box>

      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onReset={resetFilters}
        onFilterChange={onFilterChange}
        onClose={() => closeModel('filters')}
        onApply={() => onSelectedFilterChange({ ...filter })}
      >
        <CentersFilter mode="admin" filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>

      <NotDiffusedCentersModal
        centers={notDiffusedCenters}
        onClose={() => setNotDiffusedCenters([])}
      />

      {selectedData && (
        <>
          <ChangeFrenchLeaderPhoneNumberModel
            data={selectedData}
            isOpen={modalState['mobile-number-for-french-leaders']}
            onCancel={() => {
              tableRef.current?.refetch()
              closeModel('mobile-number-for-french-leaders')
            }}
          />

          <ChangeHostFamilyModel
            data={selectedData}
            isOpen={modalState['host-family']}
            onCancel={() => {
              tableRef.current?.refetch()
              closeModel('host-family')
            }}
          />
        </>
      )}
    </div>
  )
}

export default OlCenterListPage
